import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import { MedicationOptionCashPrice } from '@/types/interfaces/productDetailPage'
import {
    SessionReplayPageViewedEvents,
    trackSessionReplayPageViewed,
} from '@/lib/analytics/sessionReplayPageViewedAnalytics'
import { ensurePageViewId } from '@/lib/analytics/pageViewId'

interface MedicationOptionCashPriceParams {
    skuId: string
    /** Does not affect pricing yet; threaded through to establish the future pharmacy-aware interface. */
    zipCode?: string
}

export const medicationOptionPricingApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getMedicationOptionCashPrice: builder.query<MedicationOptionCashPrice, MedicationOptionCashPriceParams>({
            query: ({ skuId, zipCode }) => apiEndpoints.medicationOptionSkus.getCashPrice(skuId, zipCode),
            providesTags: (_result, _error, { skuId }) => [{ type: 'MedicationOptionCashPrice', id: skuId }],
            async onQueryStarted({ skuId }, { queryFulfilled }) {
                // Captured now, at query start, not after queryFulfilled resolves — a dose-form
                // switch rewrites the URL while this request is in flight, so reading the page
                // view id only after awaiting would tag this sku_id/price under whatever page
                // happens to be current by the time the network call finishes, not the one that
                // was active when this specific fetch was made.
                const pageViewId = ensurePageViewId()
                try {
                    const { data } = await queryFulfilled
                    trackSessionReplayPageViewed(
                        SessionReplayPageViewedEvents.PDP,
                        {
                            sku_id: Number(skuId),
                            gm_price_cents: data.total_price_cents ?? null,
                            // Insurance price is never resolvable on the PDP — it's pharmacy-priced
                            // and only known after a visit.
                            insurance_price_cents: null,
                        },
                        pageViewId,
                    )
                } catch {
                    // A failed price fetch still renders the PDP (MedicationPriceCard falls
                    // back to '-'), so fire the page-view without price data rather than drop it.
                    trackSessionReplayPageViewed(
                        SessionReplayPageViewedEvents.PDP,
                        {
                            sku_id: Number(skuId),
                            gm_price_cents: null,
                            insurance_price_cents: null,
                        },
                        pageViewId,
                    )
                }
            },
        }),
    }),
})

export const { useGetMedicationOptionCashPriceQuery } = medicationOptionPricingApi
